import { USAGE_WARN_RATIO } from "@sagot/constants";
import type { UsageTone } from "@sagot/ui";
import type { BillingOverview, BillingPlan } from "../features/workspace/shared";

/** What the sidebar "Messages this month" card shows, derived from `GET /v1/billing/overview`. */
export interface SidebarUsage {
  used: number;
  /** This period's message allowance; `null` on a plan with unlimited messages. */
  limit: number | null;
  /** Messages left before the limit; `null` when unlimited. */
  remaining: number | null;
  tone: UsageTone;
  /** End of the current billing period (the reset moment), `null` when the plan has none. */
  periodEnd: string | null;
}

interface OverviewSlice {
  plan: Pick<BillingOverview["plan"], "message_credits">;
  credits: Pick<BillingOverview["credits"], "used" | "allowance" | "remaining">;
  period_end: BillingOverview["period_end"];
}

function toneOf(used: number, limit: number): UsageTone {
  if (used >= limit) return "danger";
  return used >= limit * USAGE_WARN_RATIO ? "warning" : "normal";
}

export function sidebarUsageOf(overview: OverviewSlice): SidebarUsage {
  const { plan, credits } = overview;
  const base = { used: credits.used, periodEnd: overview.period_end };
  if (plan.message_credits === null) return { ...base, limit: null, remaining: null, tone: "normal" };
  return { ...base, limit: credits.allowance, remaining: credits.remaining, tone: toneOf(credits.used, credits.allowance) };
}

/**
 * Whether the catalogue has a plan above the current one (the list is ordered cheapest first, quoted
 * plans last). A plan that is not listed (e.g. a trial) has every listed plan above it.
 */
export function hasHigherPlan(plans: readonly Pick<BillingPlan, "key">[], currentKey: string): boolean {
  const current = plans.findIndex((plan) => plan.key === currentKey);
  return plans.length - 1 > current;
}
